import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Save, RefreshCw, AlertCircle, CheckCircle, Info, Eye, Loader2, User, AtSign
} from 'lucide-react';
import { senderProfileApi } from '../services/emailApi';
import { useUserContext, canWrite } from '../../../context/userContext';
import { useToast } from '../shared/ToastProvider';
import './SenderProfile.css';

/* ═══════════════════════════════════════════════════════════
   Sender Profile Page  (Settings → Email → Sender Profile)
   ═══════════════════════════════════════════════════════════
   - Manages sender name, handle, and reply-to
   - Shows live 'From:' address preview
   - GET  /api/email/sender-profile → load current settings
   - PUT  /api/email/sender-profile → save/update settings
   ═══════════════════════════════════════════════════════════ */

const HANDLE_REGEX = /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$/;
const EMAIL_REGEX  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SenderProfile = () => {
  const toast = useToast();
  const { userCtx } = useUserContext();
  const canWriteSettings = !userCtx || !userCtx.pages || canWrite(userCtx, 'email_sender');

  /* ── State ──────────────────────── */
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [profile, setProfile]   = useState(null);
  const [preview, setPreview]   = useState(null);
  const [errors, setErrors]     = useState({});
  const [dirty, setDirty]       = useState(false);

  const [form, setForm] = useState({
    senderName: '',
    senderHandle: '',
    replyToEmail: '',
  });

  /* ── Fetch current profile (GET /api/email/sender-profile) ── */
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await senderProfileApi.get();

      if (res.data?.success) {
        const d = res.data.data;
        setProfile(d);
        setForm({
          senderName:   d.senderName || '',
          senderHandle: d.senderHandle || '',
          replyToEmail: d.replyToEmail || '',
        });
        // Build preview from the same response
        setPreview({
          fromAddress: d.fromPreview || '',
          sharedDomain: d.sharedDomain || 'mail.messageos.io',
          customDomain: d.customDomain || null,
          customDomainVerified: d.customDomainVerified || false,
        });
      }
    } catch (err) {
      console.error('Sender profile fetch error:', err);
      toast.error('Failed to load sender profile');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  /* ── Handle input ───────────────── */
  const onChange = (field, value) => {
    // Force lowercase for handle
    if (field === 'senderHandle') {
      value = value.toLowerCase();
    }
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
    // Clear field error on change
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  /* ── Validate ───────────────────── */
  const validate = () => {
    const errs = {};

    if (form.senderName && form.senderName.length > 100) {
      errs.senderName = 'Maximum 100 characters';
    }

    if (form.senderHandle) {
      if (form.senderHandle.length < 2) {
        errs.senderHandle = 'Minimum 2 characters';
      } else if (!HANDLE_REGEX.test(form.senderHandle)) {
        errs.senderHandle = 'Lowercase alphanumeric and hyphens only (a-z, 0-9, -)';
      }
    }

    if (form.replyToEmail && !EMAIL_REGEX.test(form.replyToEmail)) {
      errs.replyToEmail = 'Enter a valid email address';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── Save (PUT /api/email/sender-profile) ── */
  const handleSave = async () => {
    if (!validate()) return;

    // Build payload — send strings only, never null
    const payload = {
      senderName:   form.senderName.trim(),
      senderHandle: form.senderHandle.trim().toLowerCase(),
      replyToEmail: form.replyToEmail.trim(),
    };

    // Check if anything actually changed
    const unchanged =
      payload.senderName   === (profile?.senderName || '') &&
      payload.senderHandle === (profile?.senderHandle || '') &&
      payload.replyToEmail === (profile?.replyToEmail || '');

    if (unchanged) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const res = await senderProfileApi.update(payload);

      if (res.data?.success) {
        toast.success('Sender profile updated');
        setDirty(false);
        // Refresh preview and profile
        await fetchProfile();
      } else {
        const errMsg = res.data?.error || 'Failed to update profile';
        if (res.data?.code === 'HANDLE_TAKEN') {
          setErrors(prev => ({ ...prev, senderHandle: errMsg }));
        }
        toast.error(errMsg);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'HANDLE_TAKEN') {
        setErrors(prev => ({ ...prev, senderHandle: data.error }));
        toast.error(data.error);
      } else {
        toast.error(data?.error || 'Failed to update sender profile');
      }
    } finally {
      setSaving(false);
    }
  };

  /* ── Live preview computation ───── */
  const sharedDomain = profile?.sharedDomain || preview?.sharedDomain || 'mail.messageos.io';
  const computedFrom = form.senderName
    ? `${form.senderName} <${form.senderHandle || 'info'}@${sharedDomain}>`
    : `${form.senderHandle || 'info'}@${sharedDomain}`;

  const customDomain = profile?.customDomain || preview?.customDomain;
  const customVerified = profile?.customDomainVerified || preview?.customDomainVerified;
  const computedCustom = customDomain && customVerified
    ? `${form.senderName || 'Your Brand'} <info@${customDomain}>`
    : null;

  /* ── Render ─────────────────────── */
  if (loading) {
    return (
      <div className="sp-page">
        <div className="sp-header">
          <div className="em-skl" style={{ width: 200, height: 28 }} />
          <div className="em-skl" style={{ width: 300, height: 16, marginTop: 8 }} />
        </div>
        <div className="em-card sp-form-card">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="em-input-group" style={{ marginBottom: 20 }}>
              <div className="em-skl" style={{ width: 120, height: 14 }} />
              <div className="em-skl" style={{ width: '100%', height: 40, marginTop: 6 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sp-page">
      {/* Header */}
      <div className="sp-header">
        <div>
          <h1 className="sp-title">Sender Profile</h1>
          <p className="sp-subtitle">Configure how your emails appear to recipients</p>
        </div>
        <button
          className="em-btn em-btn-secondary em-btn-sm"
          onClick={fetchProfile}
          disabled={loading}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      <div className="sp-grid">
        {/* ── Form Card ─────────── */}
        <div className="em-card sp-form-card">
          <div className="em-card-header">
            <div>
              <h2 className="em-card-title">Email From Settings</h2>
              <p className="em-card-sub">These settings define the sender details used in all outgoing emails</p>
            </div>
          </div>

          {/* Sender Name */}
          <div className="em-input-group sp-field">
            <label className="em-label">
              <User size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
              Sender Name
            </label>
            <input
              className={`em-input ${errors.senderName ? 'em-input-error' : ''}`}
              type="text"
              value={form.senderName}
              onChange={e => onChange('senderName', e.target.value)}
              placeholder="e.g. Rajesh Stores"
              maxLength={100}
            />
            {errors.senderName
              ? <p className="em-error-text">{errors.senderName}</p>
              : <p className="em-helper">Display name shown in the 'From' field (1–100 characters)</p>
            }
          </div>

          {/* Sender Handle */}
          <div className="em-input-group sp-field">
            <label className="em-label">
              <AtSign size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
              Email Handle
            </label>
            <div className="sp-handle-input">
              <input
                className={`em-input sp-handle-field ${errors.senderHandle ? 'em-input-error' : ''}`}
                type="text"
                value={form.senderHandle}
                onChange={e => onChange('senderHandle', e.target.value)}
                placeholder="e.g. rajesh"
                maxLength={50}
              />
              <span className="sp-handle-suffix">@{sharedDomain}</span>
            </div>
            {errors.senderHandle
              ? <p className="em-error-text">{errors.senderHandle}</p>
              : <p className="em-helper">Lowercase, alphanumeric + hyphens, 2–50 chars. Must be globally unique.</p>
            }
          </div>

          {/* Reply-To Email */}
          <div className="em-input-group sp-field">
            <label className="em-label">
              <Mail size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
              Reply-To Email
            </label>
            <input
              className={`em-input ${errors.replyToEmail ? 'em-input-error' : ''}`}
              type="email"
              value={form.replyToEmail}
              onChange={e => onChange('replyToEmail', e.target.value)}
              placeholder="e.g. support@rajeshstores.com"
            />
            {errors.replyToEmail
              ? <p className="em-error-text">{errors.replyToEmail}</p>
              : <p className="em-helper">When recipients hit reply, emails go to this address</p>
            }
          </div>

          {/* Save */}
          <div className="sp-actions">
            <button
              className="em-btn em-btn-primary"
              onClick={handleSave}
              disabled={saving || !dirty || !canWriteSettings}
            >
              {saving ? <Loader2 size={16} className="spin-icon" /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* ── Preview Card ────────── */}
        <div className="sp-preview-col">
          {/* From Preview */}
          <div className="em-card sp-preview-card">
            <div className="em-card-header">
              <div>
                <h2 className="em-card-title"><Eye size={15} style={{ marginRight: 6, verticalAlign: -2 }} />From Preview</h2>
                <p className="em-card-sub">How recipients see your emails</p>
              </div>
            </div>

            {/* Approach 1 — always shown */}
            <div className="sp-approach-block">
              <div className="sp-approach-header">
                <span className="em-badge em-badge-info">
                  <span className="em-badge-dot"></span>
                  Approach 1 — Shared Domain
                </span>
                {(!customVerified) && (
                  <span className="em-badge em-badge-success" style={{ fontSize: '0.6rem' }}>ACTIVE</span>
                )}
              </div>
              <div className="sp-preview-box">
                <div className="sp-preview-label">From:</div>
                <div className="sp-preview-value em-font-mono">{computedFrom}</div>
              </div>
              {form.replyToEmail && (
                <div className="sp-preview-box sp-preview-sub">
                  <div className="sp-preview-label">Reply-To:</div>
                  <div className="sp-preview-value em-font-mono">{form.replyToEmail}</div>
                </div>
              )}
              <p className="em-helper" style={{ marginTop: 8 }}>
                Uses the platform shared domain. Works immediately — no DNS changes needed.
              </p>
            </div>

            {/* Approach 2 — enterprise */}
            <div className="sp-approach-block sp-approach-enterprise">
              <div className="sp-approach-header">
                <span className="em-badge em-badge-primary">
                  <span className="em-badge-dot"></span>
                  Approach 2 — Custom Domain
                </span>
                {customVerified && (
                  <span className="em-badge em-badge-success" style={{ fontSize: '0.6rem' }}>ACTIVE</span>
                )}
                <span className="em-badge em-badge-neutral" style={{ fontSize: '0.6rem' }}>ENTERPRISE</span>
              </div>

              {customDomain ? (
                <>
                  <div className="sp-preview-box">
                    <div className="sp-preview-label">From:</div>
                    <div className="sp-preview-value em-font-mono">
                      {computedCustom || `${form.senderName || 'Your Brand'} <${form.customFromPrefix || 'info'}@${customDomain}>`}
                    </div>
                  </div>
                  {form.replyToEmail && (
                    <div className="sp-preview-box sp-preview-sub">
                      <div className="sp-preview-label">Reply-To:</div>
                      <div className="sp-preview-value em-font-mono">{form.replyToEmail}</div>
                    </div>
                  )}
                  {!customVerified && (
                    <div className="sp-status-banner sp-status-warning">
                      <AlertCircle size={14} />
                      <span>Domain pending verification. Emails currently sent from shared domain.</span>
                    </div>
                  )}
                  {customVerified && (
                    <div className="sp-status-banner sp-status-success">
                      <CheckCircle size={14} />
                      <span>Domain verified! All emails use your custom domain.</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="sp-no-domain">
                  <Info size={16} />
                  <div>
                    <p className="sp-no-domain-text">No custom domain configured</p>
                    <p className="em-helper">Set up a custom domain in Settings → Custom Domain to enable Approach 2</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Server Preview (if available) */}
          {preview?.fromAddress && (
            <div className="em-card sp-server-preview">
              <div className="sp-server-row">
                <span className="sp-server-label">Active From Address:</span>
                <span className="sp-server-value em-font-mono">{preview.fromAddress}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SenderProfile;
